/**
 * @file ページ画像の上に重ねる書き込みの層である。
 *
 * @what: 同じ枠 (study-viewer-stage) の中の画像の、実際に表示されている範囲 (object-fit: contain の
 *   余白を除いた矩形) に合わせて InkCanvas を重ねる。画像の読み込みと枠の大きさの変化に追随する。
 *   「書く」が入っていないときは表示だけで、ポインタは下の画像へ通す。
 *   過去の回を見ている間 (viewingPast) は線を受けず、紙面に触れたら「いま」に戻す (onLeavePast)。
 *   ※なぜなら見ている回に書けてしまうと、どの回に線が入ったか分からなくなるためである。
 * @why: 本に書き込みながら読む体験のため。線は論理座標で持ち、画像の縦横比に合わせた高さを使う。
 * @failure: 画像がまだ無いときは何も出さない。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { HandwritingDoc, HandwritingStroke, HandwritingTool } from "../model/handwriting";
import { InkCanvas } from "./InkCanvas";

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

function sameRect(a: Rect | null, b: Rect | null): boolean {
  if (a === null || b === null) return a === b;
  return a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height;
}

/** 画像の要素の箱の中で、実際に表示されている範囲を出す (object-fit: contain)。 */
function drawnRect(img: HTMLImageElement, stage: HTMLElement) {
  const box = img.getBoundingClientRect();
  const base = stage.getBoundingClientRect();
  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  if (nw <= 0 || nh <= 0 || box.width <= 0 || box.height <= 0) return null;
  const scale = Math.min(box.width / nw, box.height / nh);
  const w = nw * scale;
  const h = nh * scale;
  return {
    left: box.left - base.left + (box.width - w) / 2,
    top: box.top - base.top + (box.height - h) / 2,
    width: w,
    height: h,
  };
}

export function PageInkLayer({
  doc,
  onStroke,
  onAspect,
  tool,
  colorId,
  sizeId,
  enabled,
  viewingPast = false,
  onLeavePast,
}: {
  doc: HandwritingDoc;
  onStroke: (stroke: HandwritingStroke) => void;
  /** 画像の縦横比が分かったときに呼ぶ (線の無い文書の高さを合わせる)。 */
  onAspect: (naturalWidth: number, naturalHeight: number) => void;
  tool: HandwritingTool;
  colorId: string;
  sizeId: string;
  /** 「書く」が入っているか。 */
  enabled: boolean;
  /** 過去の回を見ているか (線を受けず、触れたら onLeavePast を呼ぶ)。 */
  viewingPast?: boolean;
  onLeavePast?: () => void;
}) {
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  // ペンを使ったかの印は、この層が持つ。※なぜならページを送ると中のキャンバスは作り直され、
  //   そこに置くとページごとに手のひらを受け付ける状態へ戻ってしまうためである。
  const penSeenRef = useRef(false);

  const measure = useCallback(() => {
    if (!host) return;
    const stage = host.parentElement;
    const img = stage?.querySelector("img");
    if (!stage || !(img instanceof HTMLImageElement)) {
      setRect(null);
      return;
    }
    if (!img.complete || img.naturalWidth === 0) {
      setRect(null);
      return;
    }
    onAspect(img.naturalWidth, img.naturalHeight);
    // 同じ範囲なら持ち直さない (書くたびの測り直しで、キャンバスを作り直さないため)。
    const next = drawnRect(img, stage);
    setRect((prev) => (sameRect(prev, next) ? prev : next));
  }, [host, onAspect]);

  useEffect(() => {
    if (!host) return;
    measure();
    const stage = host.parentElement;
    const img = stage?.querySelector("img");
    const onLoad = () => measure();
    img?.addEventListener("load", onLoad);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined" && stage) {
      ro = new ResizeObserver(() => measure());
      ro.observe(stage);
      if (img) ro.observe(img);
    }
    window.addEventListener("resize", measure);
    return () => {
      img?.removeEventListener("load", onLoad);
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [host, measure]);

  return (
    <div ref={setHost} className="study-page-ink" data-enabled={enabled ? "true" : "false"}>
      {rect ? (
        <InkCanvas
          doc={doc}
          onStroke={onStroke}
          tool={tool}
          colorId={colorId}
          sizeId={sizeId}
          enabled={enabled && !viewingPast}
          transparent
          penSeenRef={penSeenRef}
          ariaLabel="ページの書き込み"
          className={enabled && !viewingPast ? "study-page-ink-canvas is-writing" : "study-page-ink-canvas"}
          style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
        />
      ) : null}
      {rect && enabled && viewingPast ? (
        <div
          className="study-page-ink-past"
          role="button"
          tabIndex={0}
          aria-label="紙面に触れて「いま」の書き込みに戻る"
          style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
          onPointerDown={() => onLeavePast?.()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onLeavePast?.();
          }}
        />
      ) : null}
    </div>
  );
}
